"use client"

import React, { useState, useMemo, useEffect } from "react"
import cx from "classnames"
import { MarketTypeGroupsBySport } from "@/Overtime/constants/marketTypes"
import type { Sport } from "overtime-utils"

type OvertimeMarketTabsProps = {
  activeTab?: string
  onTabChange?: (tabId: string) => void
  className?: string
  sport?: Sport
  availableMarketTypes?: number[]
  variant?: "default" | "compact" | "pill"
  size?: "sm" | "md" | "lg"
}

const OvertimeMarketTabs: React.FC<OvertimeMarketTabsProps> = ({
  activeTab = "",
  onTabChange,
  className,
  sport,
  availableMarketTypes = [],
  variant = "default",
  size = "md",
}) => {
  const [selectedTab, setSelectedTab] = useState(activeTab)

  // Sync internal state with external activeTab prop
  useEffect(() => {
    setSelectedTab(activeTab)
  }, [activeTab])

  // Filter tabs to show only those that have available markets for the sport
  const availableTabs = useMemo(() => {
    if (sport && availableMarketTypes.length > 0) {
      // Get the market groups for this sport directly from MarketTypeGroupsBySport
      const sportGroups = MarketTypeGroupsBySport[sport] || {}

      // Create a set of available market type IDs for efficient lookup
      const availableTypeIds = new Set(availableMarketTypes)

      // Convert the keys to tab format, but only include groups that have available markets
      return Object.keys(sportGroups)
        .filter(groupKey => {
          const marketTypeIds =
            sportGroups[groupKey as keyof typeof sportGroups] || []
          // Only include groups that have at least one available market type
          return marketTypeIds.some(typeId => availableTypeIds.has(typeId))
        })
        .map(groupKey => ({
          id: groupKey.toLowerCase().replace(/\s+/g, "_"),
          label: groupKey,
        }))
    }

    // If no sport or market types provided, return empty array
    return []
  }, [sport, availableMarketTypes])

  const handleTabClick = (tabId: string) => {
    // Allow deselecting the currently active tab
    if (selectedTab === tabId) {
      setSelectedTab("")
      onTabChange?.("")
    } else {
      setSelectedTab(tabId)
      onTabChange?.(tabId)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent, tabId: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleTabClick(tabId)
    }
  }

  // If no tabs are available, don't render anything
  if (availableTabs.length === 0) {
    return null
  }

  // Size variants
  const sizeClasses = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  }

  // Variant styles
  const getVariantClasses = (isActive: boolean) => {
    switch (variant) {
      case "compact":
        return {
          container: "gap-0.5 p-0.5 bg-accent-grey-900 rounded-md",
          button: cx(
            sizeClasses[size],
            "font-medium rounded-sm transition-all duration-200 whitespace-nowrap",
            {
              "bg-white text-accent-grey-900 shadow-sm": isActive,
              "text-accent-grey-400 hover:text-accent-grey-200 hover:bg-accent-grey-800":
                !isActive,
            }
          ),
        }
      case "pill":
        return {
          container: "gap-2 p-1 bg-accent-grey-100 rounded-full",
          button: cx(
            sizeClasses[size],
            "font-medium rounded-full transition-all duration-200 whitespace-nowrap",
            {
              "bg-accent-grey-900 text-white shadow-md": isActive,
              "text-accent-grey-600 hover:text-accent-grey-800 hover:bg-accent-grey-200":
                !isActive,
            }
          ),
        }
      default:
        return {
          container: "gap-1 p-1 bg-accent-grey-900 rounded-lg",
          button: cx(
            sizeClasses[size],
            "font-medium rounded-md transition-all duration-200 whitespace-nowrap",
            {
              "bg-accent-grey-700 text-white shadow-sm": isActive,
              "text-accent-grey-300 hover:text-white hover:bg-accent-grey-800":
                !isActive,
            }
          ),
        }
    }
  }

  return (
    <div
      className={cx(
        "flex items-center overflow-x-auto no-scrollbar",
        getVariantClasses(false).container,
        className
      )}
      role="tablist"
      aria-label="Overtime market tabs"
    >
      {availableTabs.map((tab, index) => {
        const isActive = selectedTab === tab.id
        const variantClasses = getVariantClasses(isActive)

        return (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            onKeyDown={e => handleKeyDown(e, tab.id)}
            className={variantClasses.button}
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            type="button"
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export default OvertimeMarketTabs
